/**
 * Ask layer, retrieval engine.
 *
 * Part of the page guide's Ask tab (see `frontend/src/help/README.md`); from the
 * user-guide-creator skill, as in MGA.
 * HBH: `SCREEN_AREA` maps the app's routes to areas. The weights, factors, credits and thresholds
 * are the template's, calibrated against a real miss log — tune with aliases, never re-tune them.
 */
import {
  CATALOG_BASE_LANGUAGE,
  CATALOG_LANGUAGES,
  type CatalogArea,
  type CatalogEntry,
  type CatalogLanguage,
  type CatalogRole,
} from './types';

/**
 * Lexical retrieval over the capability catalog. No model, no vector store, no network call —
 * the whole index is built once from the compiled catalog and lives in memory (a hundred
 * entries is kilobytes, and a request is a few hundred microseconds).
 *
 * `retrieve()` is the seam Plan A would replace: swap this implementation for a semantic one
 * and every caller above it — service, controller, panel — keeps working, because the contract
 * is "question in, ranked entries out". Nothing downstream knows how the ranking happened.
 *
 * Two properties are deliberate rather than incidental:
 *   - **Roles boost, never filter.** An entry the user cannot act on is still the right answer
 *     to their question; it says so in `prerequisites`. Filtering would answer "no idea" to a
 *     question the catalog covers.
 *   - **An honest miss beats a bad hit.** Below the threshold the caller reports not-found. The
 *     fix for a miss is an alias or a new entry, never a lower threshold.
 */

export interface RetrieveContext {
  /** The screen the user asked from, e.g. `/orders`. Boosts that area; never filters. */
  screen?: string | null;
  language?: CatalogLanguage;
  roles?: readonly string[];
  limit?: number;
}

export interface RankedEntry {
  entry: CatalogEntry;
  /** Language of the text that matched — not necessarily the requested one. */
  language: CatalogLanguage;
  score: number;
  /** Share of the query's meaning the entry accounted for, 0–1. Drives the not-found call. */
  coverage: number;
  /**
   * Every matched term reached this entry through a spelling correction — nothing matched the word
   * as written, its stem or its prefix. Such a match is held to a higher coverage bar, because
   * guessing at one misspelled word out of two is how a recipe question finds a payment entry.
   */
  fuzzyOnly: boolean;
}

// ---------------------------------------------------------------------------- normalisation

/**
 * Diacritics are folded, so "Überprüfung" and "Uberprufung" are the same token. Users type
 * both, often in the same session, depending on the keyboard they are on. `đ` has no Unicode
 * decomposition, so it is mapped by hand; add any other letter of yours that NFD leaves alone.
 */
export const fold = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();

export const tokenize = (value: string): string[] =>
  fold(value)
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter((token) => token.length > 0);

/**
 * Question words and grammar, all catalog languages in one set — a word that carries no meaning
 * in one of them carries none in the other either. They are dropped from the QUERY only; the
 * index keeps them, because an alias like "how does this screen work" has to stay matchable as
 * a phrase.
 *
 * Without this, "how do I cancel an order from the customer's page" scores six near-worthless
 * tokens against every entry in the catalog, and the noise outweighs the four that matter.
 */
const STOPWORDS = new Set([
  // English
  'a', 'an', 'the', 'and', 'or', 'of', 'to', 'in', 'on', 'at', 'for', 'from', 'by', 'with',
  'is', 'are', 'was', 'be', 'do', 'does', 'did', 'can', 'could', 'should', 'would', 'will',
  'how', 'what', 'where', 'when', 'why', 'who', 'which', 'i', 'we', 'you', 'it', 'this',
  'that', 'my', 'me', 'us', 'there', 'here', 'if', 'as', 'so', 'not', 'no', 'yes', 'please',
  'still', 'again', 'now', 'already', 'anymore', 'only', 'just', 'also', 'more', 'most',
  'than', 'then', 'very', 'really', 'ever', 'some', 'any',
  // Verbs of ASKING, not of doing — the spoken equivalent of "how do I". "show me how to cancel
  // an order" was answered with "Find out what a screen does", because `show` matched that entry
  // strongly enough to take first place and its low coverage then declined the whole question,
  // with the right entry sitting second. What the user wants shown is in the rest of the sentence.
  'show', 'tell', 'explain',
  // 't' and 's' are what an apostrophe leaves behind — "can't", "customer's".
  't',
  // German — the example second language, folded (no umlauts: `erkläre` is stored as `erklare`).
  // Extend for a new language — question words, articles, auxiliaries, pronouns, and its
  // verbs of asking ("zeig mir", "erkläre").
  'der', 'die', 'das', 'ein', 'eine', 'einen', 'und', 'oder', 'ist', 'sind', 'hat', 'habe',
  'haben', 'wie', 'wo', 'was', 'wer', 'wann', 'warum', 'kann', 'ich', 'wir', 'mir', 'mich',
  'zu', 'mit', 'fur', 'von', 'auf', 'bei', 'an', 'nicht', 'noch', 'bitte',
  'zeig', 'zeige', 'erklare', 'erklaren', 'sag',
  // Croatian — the reference catalog's second language, kept because the list is one merged
  // set. Kept for users who type in a regional language. Harmless: a
  // stopword only matters when a user types it.
  'kako', 'sto', 'gdje', 'kada', 'kad', 'zasto', 'tko', 'koji', 'koja', 'koje', 'kojeg',
  'se', 'je', 'su', 'sam', 'si', 'smo', 'ste', 'bi', 'li', 'da', 'ne', 'ili', 'te', 'ta',
  'to', 'ovaj', 'ova', 'ovo', 'na', 'u', 'za', 'iz', 'od', 'do', 'po', 'sa', 's', 'pa',
  'mi', 'ja', 'mogu', 'moze', 'treba', 'molim', 'ako', 'koliko', 'jos', 'opet', 'samo',
  'vise', 'vec', 'onda', 'bilo',
  'pokazi', 'pokazite', 'objasni', 'reci',
]);

/**
 * Query tokens with the grammar stripped. If a question is nothing but stopwords ("how?"),
 * the raw tokens are kept — better a weak answer than pretending the question was empty.
 */
export const contentTokens = (question: string): string[] => {
  const all = tokenize(question);
  const content = all.filter((token) => !STOPWORDS.has(token));
  return content.length > 0 ? content : all;
};

/**
 * Light suffix stripping, longest suffix first, at most two passes and never below four
 * characters. It exists because inflected forms of one word — plural, case, tense — are often
 * not prefixes of one another, so prefix matching alone misses them; English plurals and
 * participles come along for free.
 *
 * Deliberately not truncation-to-N: that conflates two different words that share their first
 * letters — "customer" with "customs" — and quietly answers a question about one with the
 * other. Stripping a known suffix keeps unrelated words apart.
 *
 * The list is English plus the reference catalog's second language (a heavily inflecting
 * one). Add your language's inflection endings; drop any that is a content word's ending in
 * your domain. Keep it sorted longest-first — the `.sort` below does that for you.
 */
const SUFFIXES = [
  'ivati', 'irati', 'avati',
  'ajte', 'ajmo', 'nuti', 'jeti', 'uje',
  'ati', 'iti', 'ies', 'ing', 'ova', 'ove', 'ovi', 'ega', 'oga',
  'ala', 'ale', 'ali', 'ila', 'ile', 'ili', 'ilo', 'ite', 'imo', 'ama', 'ima',
  'ed', 'es', 'ly', 'aj', 'ao', 'la', 'lo', 'li', 'ti', 'ju', 'om', 'em', 'im', 'ih', 'og',
  'a', 'e', 'i', 'o', 'u', 'y', 's', 'j',
].sort((a, b) => b.length - a.length);

const MIN_STEM = 4;

const strippableSuffix = (value: string): string | undefined =>
  SUFFIXES.find(
    (candidate) => value.endsWith(candidate) && value.length - candidate.length >= MIN_STEM,
  );

const stem = (token: string): string => {
  let value = token;
  for (let pass = 0; pass < 2; pass += 1) {
    const suffix = strippableSuffix(value);
    if (!suffix) break;
    value = value.slice(0, -suffix.length);
  }
  return value;
};

/**
 * Damerau-Levenshtein (optimal string alignment) against a BUDGET: can `a` be turned into `b` in
 * at most `max` insertions, deletions, substitutions or transpositions of adjacent characters?
 *
 * Bounded rather than exact, because the only question ever asked of it is "close enough?" — so it
 * exits as soon as every remaining alignment already costs more than the budget, and answers from
 * the length difference alone when it can. Also used by `interaction-log.ts`, where the same
 * question is "did the user just correct a typo, or ask something else?".
 */
export const editDistanceWithin = (a: string, b: string, max: number): boolean => {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > max) return false;
  if (max < 1) return false;

  // Three rows rotated rather than a full matrix: the recurrence never looks further back than
  // two (transposition), and typed arrays keep the inner loop free of both allocation and the
  // undefined-checks `noUncheckedIndexedAccess` requires of a `number[]`.
  const width = b.length + 1;
  let beforePrevious = new Uint16Array(width);
  let previous = new Uint16Array(width);
  let row = new Uint16Array(width);
  for (let j = 0; j < width; j += 1) previous[j] = j;

  // Every index below is bounded by the loops themselves — hence the assertions, which are what
  // `noUncheckedIndexedAccess` costs on a hot numeric loop.
  for (let i = 1; i <= a.length; i += 1) {
    row[0] = i;
    let rowMin = i;
    for (let j = 1; j <= b.length; j += 1) {
      const substitution = a[i - 1] === b[j - 1] ? 0 : 1;
      let cost = Math.min(previous[j]! + 1, row[j - 1]! + 1, previous[j - 1]! + substitution);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        cost = Math.min(cost, beforePrevious[j - 2]! + 1);
      }
      row[j] = cost;
      if (cost < rowMin) rowMin = cost;
    }
    // Every alignment through this row is already too expensive; nothing below can recover.
    if (rowMin > max) return false;
    const reusable = beforePrevious;
    beforePrevious = previous;
    previous = row;
    row = reusable;
  }
  return previous[b.length]! <= max;
};

/**
 * How wrong a word may be spelled and still reach its entry.
 *
 * One edit was too strict for what users actually mistype. The first gap report was six rows of
 * one word: `invoicce` (one edit — a doubled letter), `invoicces` (two — doubled letter *and* the
 * plural), `invoiccces` (three). A doubled consonant plus an inflection is the ordinary shape of
 * a typo in most languages, and it costs two.
 *
 * Capped at two, and never scaled with length, because three is where this stops being typo
 * recovery and starts inventing synonyms: `customer` and `customize` are three edits apart, and
 * a budget that reaches them would answer questions about one with the other. Short words keep
 * the budget of one for the same reason — `order` is already a single edit from `older`.
 */
export const fuzzyBudget = (length: number): number => (length >= 6 ? 2 : 1);

/**
 * Below four characters a word carries too little signal to correct: at three, every budget of one
 * reaches half the vocabulary. `ord` therefore stays a miss, which is right — it is not a question.
 */
const FUZZY_MIN_LENGTH = 4;

// ---------------------------------------------------------------------------- the index

/** Field weights: what a user types is much closer to a job title than to a description. */
const FIELD_WEIGHT = {
  job: 3.2,
  alias: 2.6,
  prerequisites: 1.1,
  description: 1,
  area: 0.7,
} as const;

type Field = keyof typeof FIELD_WEIGHT;

interface Document {
  entry: CatalogEntry;
  language: CatalogLanguage;
  /** token → summed field weight of its occurrences. */
  weights: Map<string, number>;
  stems: Map<string, number>;
  /** Folded job + aliases, for the verbatim phrase boost. */
  phrases: string[];
  /** The same aliases as STEM lists, for the any-order boost. */
  aliasStems: string[][];
  length: number;
}

export interface CatalogIndex {
  documents: Document[];
  /** token → number of documents containing it, for IDF. */
  df: Map<string, number>;
  vocabulary: string[];
  averageLength: number;
  entriesById: Map<string, CatalogEntry>;
}

const addTokens = (doc: Document, text: string | undefined, field: Field): void => {
  if (!text) return;
  const weight = FIELD_WEIGHT[field];
  for (const token of tokenize(text)) {
    doc.weights.set(token, (doc.weights.get(token) ?? 0) + weight);
    const stemmed = stem(token);
    doc.stems.set(stemmed, (doc.stems.get(stemmed) ?? 0) + weight);
    doc.length += 1;
  }
};

/**
 * One document per (entry, language): a German question has to be able to reach an entry
 * through its German text alone, without the English text diluting the term statistics.
 */
export const buildIndex = (entries: readonly CatalogEntry[]): CatalogIndex => {
  const documents: Document[] = [];
  const entriesById = new Map<string, CatalogEntry>();

  for (const entry of entries) {
    if (entry.status !== 'published') continue;
    entriesById.set(entry.id, entry);

    for (const language of CATALOG_LANGUAGES) {
      const translation = language === CATALOG_BASE_LANGUAGE ? entry : entry.i18n?.[language];
      if (!translation) continue;

      const doc: Document = {
        entry,
        language,
        weights: new Map(),
        stems: new Map(),
        phrases: [],
        aliasStems: [],
        length: 0,
      };
      addTokens(doc, translation.job, 'job');
      addTokens(doc, translation.description, 'description');
      addTokens(doc, translation.prerequisites, 'prerequisites');
      addTokens(doc, entry.area, 'area');
      for (const alias of translation.aliases ?? []) addTokens(doc, alias, 'alias');

      doc.phrases = [translation.job, ...(translation.aliases ?? [])].map(fold);
      doc.aliasStems = (translation.aliases ?? [])
        .map((alias) => tokenize(alias).map(stem))
        .filter((tokens) => tokens.length > 1);
      documents.push(doc);
    }
  }

  const df = new Map<string, number>();
  for (const doc of documents) {
    for (const token of Array.from(doc.weights.keys())) df.set(token, (df.get(token) ?? 0) + 1);
  }

  const totalLength = documents.reduce((sum, doc) => sum + doc.length, 0);
  return {
    documents,
    df,
    vocabulary: Array.from(df.keys()),
    averageLength: documents.length > 0 ? totalLength / documents.length : 1,
    entriesById,
  };
};

// ---------------------------------------------------------------------------- scoring

const K1 = 1.2;
const B = 0.55;

/**
 * Screens map to areas so "how do I export this?" asked from the customers list ranks the
 * customers area first. Prefix patterns, first match wins.
 * HBH routes → CATALOG_AREAS.
 */
const SCREEN_AREA: ReadonlyArray<readonly [RegExp, CatalogArea]> = [
  [/^\/admin/, 'catalog'],
  [/^\/country/, 'catalog'],
  // /company-profile/:categoryId/:implementationId/:countryCode/:client — a package list
  [/^\/company-profile\/[^/]+\/[^/]+\/[^/]+\/[^/]+/, 'packages'],
  // /company-profile/:categoryId/:countryCode/:client — an implementation list
  [/^\/company-profile\/[^/]+\/[^/]+\/[^/]+/, 'implementations'],
  [/^\/company-profile/, 'clients'],
  [/^\/hr\/needs-attention/, 'enrollments'],
  [/^\/hr\/packages/, 'packages'],
  [/^\/hr/, 'employees'],
];

export const areaOfScreen = (screen: string | null | undefined): CatalogArea | null => {
  if (!screen) return null;
  for (const [pattern, area] of SCREEN_AREA) if (pattern.test(screen)) return area;
  return null;
};

const AREA_BOOST = 1.18;
const ROLE_BOOST = 1.12;
const REQUESTED_LANGUAGE_BOOST = 1.15;
const PHRASE_BOOST = 1.45;
const ALIAS_BOOST = 1.3;
const STEM_FACTOR = 0.45;
const PREFIX_FACTOR = 0.6;
const FUZZY_FACTOR = 0.3;

/**
 * Coverage credit per tier — how much of a query term's meaning the match accounted for, as
 * opposed to how certain the match was (`*_FACTOR`, above). All three sit clear of `MIN_COVERAGE`
 * on purpose: a term that was RECOGNISED, however loosely, is not a term the entry failed to
 * address, and the decision "is this an answer at all?" must not turn on spelling.
 *
 * Certainty is still discounted, in the score. An entry reached through three typos ranks below
 * one reached through three exact words, and `MIN_SCORE` remains the honest floor — this changes
 * which questions are *considered*, not how confidently they are answered.
 */
const STEM_CREDIT = 0.85;
const PREFIX_CREDIT = 0.9;
const FUZZY_CREDIT = 0.8;

/**
 * Minimum score and coverage for an answer to be shown at all. Tuned against
 * `catalog/golden-questions.json` (`npm run assistant:eval`) — raise them and real questions go
 * unanswered, lower them and the panel starts confidently answering the wrong thing.
 *
 * Both are load-bearing, and the eval's declined cases prove it (measured on the reference
 * catalog of ~80 entries): "book me a flight to London" scores 9.51 — higher than several real
 * questions — and is stopped only by coverage 0.25, while a second-language "what time is it"
 * covers 0.80 of itself and is stopped only by its score of 2.03.
 */
export const MIN_SCORE = 3;
export const MIN_COVERAGE = 0.38;

/**
 * The floor when the ONLY evidence is spelling correction — every matched term reached its entry
 * through the fuzzy tier and none matched exactly, by stem or by prefix.
 *
 * Letting typos through needed this, and a global threshold could not have provided it. Measured
 * on the reference catalog: `invioce` (a real question, must answer) scores 4.25; a recipe
 * question whose one word happens to sit one edit from a catalog word (`recept` ↔ `receipt`) and
 * must decline scores 3.33. No score floor separates those without also declining the real
 * question; no coverage floor separates 0.80 from 0.40 without declining four real questions
 * that sit at 0.43–0.50.
 *
 * What actually separates them is not a magnitude but a shape: for `invioce` the corrected word
 * IS the whole question, for the recipe it is half of one. So a correction may answer only when
 * it accounts for essentially all of the question. Fuzzy credit is 0.8 per term, so all-fuzzy
 * coverage is 0.8 when every term matched and drops proportionally when any did not — 0.7 is the
 * line between "every word was recognised" and "one of two was".
 */
export const MIN_FUZZY_COVERAGE = 0.7;

const idf = (index: CatalogIndex, token: string): number => {
  const n = index.documents.length;
  const df = index.df.get(token) ?? 0;
  // BM25's IDF, floored: a term in every document contributes ~nothing, never a negative score.
  return Math.max(0.05, Math.log(1 + (n - df + 0.5) / (df + 0.5)));
};

interface TokenHit {
  /** Summed field weight of the matching term in this document. */
  weight: number;
  /**
   * How solid the match was: 1 for the term itself, less for a stem, a prefix or a typo.
   *
   * Applied AFTER saturation, not to the weight, and that ordering is the whole point. Multiply
   * the weight instead and a term repeated across job, aliases and description can make a stem
   * collision — two unrelated words reduced to the same stem — outscore an exact hit somewhere
   * else. A partial match must never be worth more than a solid one, however often it occurs.
   */
  factor: number;
  /**
   * How much of the query term's MEANING the entry accounted for. Feeds coverage, which decides
   * not-found; `factor` feeds the score, which decides rank. They are not the same judgement:
   * a misspelled word that was recognised accounts for its meaning in full, while remaining a
   * less certain match than the word spelled right.
   *
   * Conflating them made the typo tier unreachable rather than merely weak. For a one-word
   * question, coverage collapses to exactly this token's factor — so a fuzzy hit scored 0.3
   * against a floor of 0.38 and was declined *however well it matched*. `invioce` was recognised
   * as `invoice`, ranked first, and reported as "no answer" six times in the first day of
   * telemetry. A gate that cannot be passed is not a gate, it is a dead branch.
   */
  credit: number;
  /** Which tier produced the match. Read by the caller to tell a correction from a real word. */
  tier: 'exact' | 'stem' | 'prefix' | 'fuzzy' | 'none';
}

const NO_HIT: TokenHit = { weight: 0, factor: 0, credit: 0, tier: 'none' };

/** Best match this document offers for one query token, and how it was reached. */
const tokenWeight = (doc: Document, token: string): TokenHit => {
  const exact = doc.weights.get(token);
  if (exact !== undefined) return { weight: exact, factor: 1, credit: 1, tier: 'exact' };

  let best = NO_HIT;
  const consider = (weight: number, factor: number, credit: number, tier: TokenHit['tier']): void => {
    if (weight * factor > best.weight * best.factor) best = { weight, factor, credit, tier };
  };

  const stemmed = doc.stems.get(stem(token));
  if (stemmed !== undefined) consider(stemmed, STEM_FACTOR, STEM_CREDIT, 'stem');

  if (token.length >= 4) {
    for (const [candidate, weight] of Array.from(doc.weights)) {
      if (candidate.length > token.length && candidate.startsWith(token)) {
        consider(weight, PREFIX_FACTOR, PREFIX_CREDIT, 'prefix');
      }
    }
  }
  // Last resort, and only for a token nothing else could reach: a spelling correction is evidence
  // about what the user meant, never a reason to outrank the word they got right.
  if (best.factor === 0 && token.length >= FUZZY_MIN_LENGTH) {
    const budget = fuzzyBudget(token.length);
    for (const [candidate, weight] of Array.from(doc.weights)) {
      if (editDistanceWithin(token, candidate, budget)) {
        consider(weight, FUZZY_FACTOR, FUZZY_CREDIT, 'fuzzy');
      }
    }
  }
  return best;
};

export const retrieve = (
  index: CatalogIndex,
  question: string,
  context: RetrieveContext = {},
): RankedEntry[] => {
  const tokens = Array.from(new Set(contentTokens(question)));
  if (tokens.length === 0) return [];

  const folded = fold(question).replace(/\s+/g, ' ').trim();
  const queryStems = new Set(tokenize(question).map(stem));
  const area = areaOfScreen(context.screen);
  const roles = new Set(context.roles ?? []);
  const requested = context.language ?? CATALOG_BASE_LANGUAGE;
  const limit = context.limit ?? 5;

  const idfByToken = new Map(tokens.map((token) => [token, idf(index, token)]));
  const totalIdf = Array.from(idfByToken.values()).reduce((sum, value) => sum + value, 0) || 1;

  const scored: RankedEntry[] = [];

  for (const doc of index.documents) {
    let score = 0;
    let matchedIdf = 0;
    let solidHits = 0;

    for (const token of tokens) {
      const hit = tokenWeight(doc, token);
      if (hit.factor === 0) continue;
      if (hit.tier !== 'fuzzy') solidHits += 1;
      const saturated =
        (hit.weight * (K1 + 1)) /
        (hit.weight + K1 * (1 - B + (B * doc.length) / (index.averageLength || 1)));
      const idfOfToken = idfByToken.get(token) ?? 0;
      score += idfOfToken * saturated * hit.factor;
      matchedIdf += idfOfToken * hit.credit;
    }
    if (score === 0) continue;

    // A question that repeats a job title or an alias almost verbatim is not a bag of words.
    if (doc.phrases.some((phrase) => phrase.includes(folded) || folded.includes(phrase))) {
      score *= PHRASE_BOOST;
    } else if (doc.aliasStems.some((alias) => alias.every((token) => queryStems.has(token)))) {
      // Every word of an alias present in the question, in any order and in any inflection:
      // "cancel order" inside "how do I cancel an order I placed yesterday", or
      // "neue bestellung" inside "wie lege ich eine neue bestellung an".
      score *= ALIAS_BOOST;
    }
    if (area && doc.entry.area === area) score *= AREA_BOOST;
    if (doc.language === requested) score *= REQUESTED_LANGUAGE_BOOST;
    if (doc.entry.roles.some((role: CatalogRole) => roles.has(role))) score *= ROLE_BOOST;

    scored.push({
      entry: doc.entry,
      language: doc.language,
      score,
      coverage: matchedIdf / totalIdf,
      fuzzyOnly: solidHits === 0,
    });
  }

  // One row per entry — the same job matching in two languages is one answer, not two.
  const best = new Map<string, RankedEntry>();
  for (const candidate of scored) {
    const current = best.get(candidate.entry.id);
    if (!current || candidate.score > current.score) best.set(candidate.entry.id, candidate);
  }

  return Array.from(best.values())
    .sort((a, b) => b.score - a.score || a.entry.id.localeCompare(b.entry.id))
    .slice(0, limit);
};

/** Whether the top hit is good enough to show as an answer rather than a not-found. */
export const isConfident = (hit: RankedEntry | undefined): boolean =>
  !!hit &&
  hit.score >= MIN_SCORE &&
  hit.coverage >= (hit.fuzzyOnly ? MIN_FUZZY_COVERAGE : MIN_COVERAGE);
